<script setup>
// A team's current handicap as a small chip. Labels come from the competition's rendered
// settings (see useHandicaps) — pass the settings object, never a sport.
import {computed} from 'vue';
import {useHandicaps} from '~/composables/useHandicaps';

const props = defineProps({
  settings: {type: Object, default: null},
  value: {type: [Number, String], default: null},
  // Show "Not set" for an unseeded team (admin surfaces) instead of rendering nothing.
  showUnset: {type: Boolean, default: false},
  size: {type: String, default: 'small'},
});
const {handicapsEnabled, labelFor, chipClassFor} = useHandicaps();
const label = computed(() => labelFor(props.settings, props.value));
const visible = computed(() => handicapsEnabled(props.settings) && (label.value != null || props.showUnset));
const tooltip = computed(() => {
  const unit = props.settings?.scheme?.notation === 1 ? 'points' : (props.settings?.scheme?.unitPlural ?? 'units');
  const per = props.settings?.scheme?.appliesTo ?? 'game';
  return label.value == null
      ? 'No handicap set yet'
      : `Handicap ${label.value} (${unit} per ${per.replace('the ', '')})`;
});
</script>

<template>
  <v-tooltip v-if="visible" :text="tooltip" location="top">
    <template #activator="{ props: tip }">
      <span v-bind="tip" class="tl-chip hc-chip" :class="[label == null ? 'hc-chip--unset' : chipClassFor(settings, value), `hc-chip--${size}`]">
        <v-icon size="11" icon="mdi-scale-balance"/>
        <span>{{ label ?? 'Not set' }}</span>
      </span>
    </template>
  </v-tooltip>
</template>

<style scoped lang="scss">
.hc-chip {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 1px 7px;
  border-radius: 999px;
  line-height: 1.4;
  white-space: nowrap;
  font-weight: 600;

  &--unset {
    color: rgb(var(--v-theme-textSubtle));
    border: 1px dashed rgb(var(--v-theme-borderLight));
    background: transparent;
  }

  &--x-small { font-size: 10px; padding: 0 6px; }
}
</style>
